import type { DestinationVerdict, GuideShape } from "../../trainer/boardGuides.ts";

/**
 * Draws the board guides' shapes into cm-chessboard's own SVG, using the
 * board view's geometry so they follow orientation and resizing. Fills sit
 * under the pieces; rings, badges, arrows and lines sit over them. Every
 * element carries data-guide-layer so observers can tell our redraws apart
 * from the board's.
 */
type BoardView = {
  svg: SVGSVGElement;
  piecesLayer: SVGGElement;
  interactiveTopLayer: SVGGElement;
  squareWidth: number;
  squareHeight: number;
  squareToPoint: (square: string) => { x: number; y: number };
};

const SVG_NS = "http://www.w3.org/2000/svg";
const TONES = ["danger", "caution", "target", "info", "mine", "theirs"] as const;

function element<K extends keyof SVGElementTagNameMap>(
  name: K,
  attributes: Record<string, string | number>,
  parent?: Element
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  parent?.appendChild(node);
  return node;
}

export function clearGuideLayer(view: BoardView | undefined): void {
  view?.svg
    ?.querySelectorAll('[data-guide-layer="under"], [data-guide-layer="over"]')
    .forEach((node) => node.remove());
}

export function clearMoveHints(view: BoardView | undefined): void {
  view?.svg?.querySelectorAll('[data-guide-layer="moves"]').forEach((node) => node.remove());
}

/**
 * The safe-squares guide for a piece the student has picked up: a dot on each
 * empty destination and a ring on each capture, coloured by what the move
 * does to the material balance.
 */
export function paintMoveHints(view: BoardView | undefined, verdicts: DestinationVerdict[]): void {
  if (!view?.svg || !view.squareToPoint) return;
  clearMoveHints(view);
  if (!verdicts.length) return;
  const w = view.squareWidth;
  const layer = element("g", { "data-guide-layer": "moves", class: "guide-layer" });
  view.svg.insertBefore(layer, view.interactiveTopLayer);
  for (const verdict of verdicts) {
    const point = view.squareToPoint(verdict.to);
    const cx = point.x + w / 2;
    const cy = point.y + w / 2;
    const tone = `verdict-${verdict.verdict}`;
    if (verdict.capture || verdict.verdict === "wins" || verdict.verdict === "mate") {
      element(
        "circle",
        { cx, cy, r: w * 0.44, "stroke-width": w * 0.08, class: `guide-move-ring ${tone}` },
        layer
      );
    }
    if (!verdict.capture) {
      element("circle", { cx, cy, r: w * 0.15, class: `guide-move-dot ${tone}` }, layer);
    }
  }
}

export function paintGuideLayer(view: BoardView | undefined, shapes: GuideShape[]): void {
  if (!view?.svg || !view.squareToPoint) return;
  clearGuideLayer(view);
  if (!shapes.length) return;
  const w = view.squareWidth;
  const under = element("g", { "data-guide-layer": "under", class: "guide-layer" });
  const over = element("g", { "data-guide-layer": "over", class: "guide-layer" });
  view.svg.insertBefore(under, view.piecesLayer);
  view.svg.insertBefore(over, view.interactiveTopLayer);

  const defs = element("defs", {}, over);
  for (const tone of TONES) {
    const marker = element(
      "marker",
      {
        id: `guide-arrow-${tone}`,
        viewBox: "0 0 10 10",
        refX: 5,
        refY: 5,
        markerWidth: 3.2,
        markerHeight: 3.2,
        orient: "auto-start-reverse",
      },
      defs
    );
    element("path", { d: "M 0 0 L 10 5 L 0 10 z", class: `guide-arrowhead tone-${tone}` }, marker);
  }

  const center = (square: string) => {
    const point = view.squareToPoint(square);
    return { x: point.x + w / 2, y: point.y + w / 2 };
  };

  for (const shape of shapes) {
    if (shape.kind === "fill") {
      const point = view.squareToPoint(shape.square);
      element(
        "rect",
        {
          x: point.x,
          y: point.y,
          width: w,
          height: view.squareHeight,
          class: `guide-fill tone-${shape.tone}${shape.strength === 2 ? " is-strong" : ""}`,
        },
        under
      );
    } else if (shape.kind === "ring") {
      const c = center(shape.square);
      element(
        "circle",
        {
          cx: c.x,
          cy: c.y,
          r: w * 0.45,
          "stroke-width": w * 0.06,
          class: `guide-ring tone-${shape.tone}${shape.dashed ? " is-dashed" : ""}`,
        },
        over
      );
    } else if (shape.kind === "badge") {
      const point = view.squareToPoint(shape.square);
      const cx = point.x + w * 0.8;
      const cy = point.y + w * 0.2;
      element("circle", { cx, cy, r: w * 0.17, class: `guide-badge tone-${shape.tone}` }, over);
      const text = element(
        "text",
        {
          x: cx,
          y: cy,
          "font-size": w * (shape.text.length > 1 ? 0.15 : 0.2),
          "text-anchor": "middle",
          "dominant-baseline": "central",
          class: "guide-badge-text",
        },
        over
      );
      text.textContent = shape.text;
    } else {
      const from = center(shape.from);
      const to = center(shape.to);
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy) || 1;
      // Stop an arrow short of the target square's centre so the head shows.
      const trim = shape.kind === "arrow" ? w * 0.28 : 0;
      const line = {
        x1: from.x,
        y1: from.y,
        x2: to.x - (dx / length) * trim,
        y2: to.y - (dy / length) * trim,
      };
      const width = shape.kind === "arrow" ? w * 0.1 : w * 0.05;
      // A light halo under the stroke keeps it readable on dark squares.
      element("line", { ...line, "stroke-width": width * 1.9, class: "guide-halo" }, over);
      element(
        "line",
        {
          ...line,
          "stroke-width": width,
          class: `guide-${shape.kind} tone-${shape.tone}`,
          ...(shape.kind === "arrow" ? { "marker-end": `url(#guide-arrow-${shape.tone})` } : {}),
        },
        over
      );
    }
  }
}

/** True when every node a mutation touched belongs to the guide layer. */
export function isGuideMutation(mutation: MutationRecord): boolean {
  const ours = (node: Node) =>
    node instanceof Element &&
    (node.hasAttribute("data-guide-layer") || Boolean(node.closest("[data-guide-layer]")));
  const nodes = [...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)];
  return nodes.length > 0 ? nodes.every(ours) : ours(mutation.target);
}
